import { useCallback, useEffect, useRef, useState } from 'react';
import type { WorkspaceMemberInfo } from '../workspaces/workspacesApi';
import type { BoardColumn } from '../boardColumns/boardColumnsApi';
import type { Label } from '../labels/labelsApi';
import { epicsApi, type Epic } from '../epics/epicsApi';
import { projectsApi, type Project } from '../projects/projectsApi';
import { tasksApi, type Task } from '../tasks/tasksApi';

type GetToken = () => string | null;
type DoRefresh = () => Promise<boolean>;

interface ImportTasksPanelProps {
  workspaceId: string;
  sprintId: string;
  /** Set of taskIds already in the sprint — shown as checked/disabled. */
  sprintTaskIds: Set<string>;
  columns: BoardColumn[];
  labels: Label[];
  members: WorkspaceMemberInfo[];
  getToken: GetToken;
  doRefresh: DoRefresh;
  /** Called when user clicks a task row (open task detail panel). */
  onTaskClick: (task: Task) => void;
  /** Called with the selected task IDs — parent handles the actual API calls. */
  onImport: (taskIds: string[]) => Promise<void>;
  onCancel: () => void;
}

export default function ImportTasksPanel({
  workspaceId,
  sprintId: _sprintId,
  sprintTaskIds,
  columns,
  labels,
  members,
  getToken,
  doRefresh,
  onTaskClick,
  onImport,
  onCancel,
}: ImportTasksPanelProps) {
  const [epicId, setEpicId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [epics, setEpics] = useState<Epic[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  const masterCheckboxRef = useRef<HTMLInputElement>(null);

  // Load epics once
  useEffect(() => {
    epicsApi.list(workspaceId, getToken, doRefresh)
      .then(d => setEpics(d.epics))
      .catch(() => setError('Failed to load epics'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEpicChange = useCallback((id: string) => {
    setEpicId(id);
    setProjectId('');
    setProjects([]);
    setTasks([]);
    setSelectedIds([]);
    if (!id) return;
    setLoadingProjects(true);
    projectsApi.list(workspaceId, id, getToken, doRefresh)
      .then(d => setProjects(d.projects))
      .catch(() => setError('Failed to load projects'))
      .finally(() => setLoadingProjects(false));
  }, [workspaceId, getToken, doRefresh]);

  const handleProjectChange = useCallback((id: string) => {
    setProjectId(id);
    setTasks([]);
    setSelectedIds([]);
    if (!id) return;
    setLoadingTasks(true);
    tasksApi.list(workspaceId, id, getToken, doRefresh)
      .then(d => setTasks(d.tasks))
      .catch(() => setError('Failed to load tasks'))
      .finally(() => setLoadingTasks(false));
  }, [workspaceId, getToken, doRefresh]);

  // Derived
  const importableTasks = tasks.filter(t => !sprintTaskIds.has(t.taskId));
  const allSelected = importableTasks.length > 0 && importableTasks.every(t => selectedIds.includes(t.taskId));
  const someSelected = selectedIds.length > 0 && !allSelected;

  // Master checkbox indeterminate state
  useEffect(() => {
    if (masterCheckboxRef.current) {
      masterCheckboxRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  function toggleSelect(taskId: string) {
    setSelectedIds(prev =>
      prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId],
    );
  }

  function selectAll() {
    setSelectedIds(importableTasks.map(t => t.taskId));
  }

  function deselectAll() {
    setSelectedIds([]);
  }

  async function handleImport() {
    if (selectedIds.length === 0) return;
    setImporting(true);
    setError('');
    try {
      await onImport(selectedIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
      setImporting(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Epic + project selectors */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Epic</label>
          <select
            value={epicId}
            onChange={e => handleEpicChange(e.target.value)}
            style={{ fontSize: 13 }}
          >
            <option value="">Select epic…</option>
            {epics.map(ep => <option key={ep.epicId} value={ep.epicId}>{ep.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Project</label>
          <select
            value={projectId}
            onChange={e => handleProjectChange(e.target.value)}
            disabled={!epicId || loadingProjects}
            style={{ fontSize: 13 }}
          >
            <option value="">{loadingProjects ? 'Loading…' : 'Select project…'}</option>
            {projects.map(p => <option key={p.projectId} value={p.projectId}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {/* Task list sub-header (select all / deselect all) */}
      {tasks.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 16px',
            borderBottom: '1px solid var(--color-border)',
            flexShrink: 0,
            background: 'var(--color-surface-secondary, var(--color-surface))',
          }}
        >
          <input
            ref={masterCheckboxRef}
            type="checkbox"
            checked={allSelected}
            onChange={e => e.target.checked ? selectAll() : deselectAll()}
            disabled={importableTasks.length === 0}
            style={{ flexShrink: 0, cursor: importableTasks.length > 0 ? 'pointer' : 'default' }}
          />
          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            {importableTasks.length === 0
              ? 'All tasks already in sprint'
              : `${selectedIds.length} of ${importableTasks.length} selected`}
          </span>
          {selectedIds.length > 0 && (
            <button
              type="button"
              onClick={deselectAll}
              style={{ marginLeft: 'auto', fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '2px 4px' }}
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Task list — scrollable */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {!projectId && (
          <p className="muted" style={{ padding: '20px 16px', textAlign: 'center', fontSize: 13 }}>
            Select an epic and project to browse tasks.
          </p>
        )}
        {projectId && loadingTasks && (
          <p className="muted" style={{ padding: '20px 16px', textAlign: 'center', fontSize: 13 }}>Loading tasks…</p>
        )}
        {projectId && !loadingTasks && tasks.length === 0 && (
          <p className="muted" style={{ padding: '20px 16px', textAlign: 'center', fontSize: 13 }}>No tasks in this project.</p>
        )}
        {tasks.map(task => {
          const inSprint = sprintTaskIds.has(task.taskId);
          const selected = selectedIds.includes(task.taskId);
          const taskLabels = labels.filter(l => task.labelIds.includes(l.labelId));
          const borderColor = taskLabels[0]?.color ?? 'var(--color-border)';
          const assignees = members.filter(m => task.assigneeIds.includes(m.userId));
          const statusName = columns.find(c => c.columnId === task.status)?.name ?? task.status;

          return (
            <div
              key={task.taskId}
              role="button"
              tabIndex={0}
              onClick={() => onTaskClick(task)}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onTaskClick(task)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 16px',
                borderLeft: `3px solid ${borderColor}`,
                borderBottom: '1px solid var(--color-border)',
                cursor: 'pointer',
                background: selected ? 'rgba(59,130,246,0.06)' : inSprint ? 'rgba(34,197,94,0.04)' : undefined,
                outline: 'none',
                userSelect: 'none',
              }}
            >
              {/* Checkbox */}
              <div onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }}>
                {inSprint ? (
                  <input
                    type="checkbox"
                    checked
                    disabled
                    title="Already in this sprint"
                    style={{ accentColor: '#22c55e', cursor: 'not-allowed' }}
                  />
                ) : (
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleSelect(task.taskId)}
                    style={{ cursor: 'pointer' }}
                  />
                )}
              </div>

              {/* Title */}
              <span
                style={{
                  flex: 1,
                  fontSize: 13,
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: inSprint ? 'var(--color-text-muted)' : undefined,
                }}
              >
                {task.title}
              </span>

              {/* Badges */}
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
                {inSprint && (
                  <span style={{ fontSize: 10, color: '#22c55e', fontWeight: 600, background: 'rgba(34,197,94,0.12)', borderRadius: 10, padding: '1px 6px', whiteSpace: 'nowrap' }}>
                    In Sprint
                  </span>
                )}
                <span
                  style={{
                    fontSize: 10,
                    background: 'var(--color-border)',
                    borderRadius: 10,
                    padding: '1px 6px',
                    color: 'var(--color-text-muted)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {statusName}
                </span>
                {task.points > 0 && (
                  <span className="badge" style={{ fontSize: 10, padding: '1px 5px' }}>{task.points}pt</span>
                )}
                {assignees.length > 0 && (
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                    {assignees.map(a => a.displayName.split(' ')[0]).join(', ')}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {error && <p className="error" style={{ margin: 0, padding: '8px 16px', fontSize: 12, flexShrink: 0 }}>{error}</p>}

      {/* Sticky footer */}
      <div
        style={{
          borderTop: '1px solid var(--color-border)',
          padding: '12px 16px',
          flexShrink: 0,
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          background: 'var(--color-surface)',
        }}
      >
        <button
          type="submit"
          disabled={selectedIds.length === 0 || importing}
          onClick={handleImport}
        >
          {importing ? 'Importing…' : `Import${selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{ background: 'none' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
